import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/BusinessLogo.png';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';
import { ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';

const logoVariants = {
  initial: {
    filter: 'drop-shadow(0 0 0px rgba(255,255,255,0))',
  },
  animate: {
    filter: [
      'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
      'drop-shadow(0 0 15px rgba(255,255,255,0.6))',
      'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

const Navbar = ({ isRegisterPage, Translated }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();
  const [hoveredLink, setHoveredLink] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };
  return (
    <motion.nav
      className={`bg-booker font-hurme relative ${isRTL ? 'rtl' : 'ltr'}`}
      initial={{
        borderBottomLeftRadius: '4vh',
        borderBottomRightRadius: '4vh',
      }}
      animate={
        isMenuOpen
          ? {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
          : {
              borderBottomLeftRadius: '4vh',
              borderBottomRightRadius: '4vh',
            }
      }
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}>
      {/* Desktop Navigation */}
      <div className='hidden lg:flex justify-between items-center h-[105px]'>
        {/* Side Navigation Links */}
        <div
          className={`flex items-center text-xl mt-16 w-1/3 ${
            isRTL ? 'pr-48' : 'pl-48'
          } `}>
          {isRegisterPage && (
            <div
              className={`flex ${
                isRTL ? 'space-x-reverse space-x-44' : 'space-x-44'
              }`}>
              <Link
                to='/'
                className='text-white font-hurmeSemiBold pb-2 relative'
                onMouseEnter={() => setHoveredLink('about')}
                onMouseLeave={() => setHoveredLink(null)}>
                {t('nav.home')}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transform transition-all duration-300 ${
                    hoveredLink === 'about' ? 'scale-x-100' : 'scale-x-0'
                  } origin-left`}></span>
              </Link>
              {/* <Link
                 to='/join'
                 className='text-white font-hurmeSemiBold pb-2 relative'
                 onMouseEnter={() => setHoveredLink('join')}
                 onMouseLeave={() => setHoveredLink(null)}>
                 {t('nav.join')}
                 <span
                   className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transform transition-all duration-300 ${
                     hoveredLink === 'join' ? 'scale-x-100' : 'scale-x-0'
                   } origin-left`}></span>
               </Link> */}
              <Link
                to='https://thebooker.online/#business'
                className='text-white font-hurmeSemiBold pb-2 relative'
                onMouseEnter={() => setHoveredLink('pricing')}
                onMouseLeave={() => setHoveredLink(null)}>
                {t('nav.pricing')}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transform transition-all duration-300 ${
                    hoveredLink === 'pricing' ? 'scale-x-100' : 'scale-x-0'
                  } origin-left`}></span>
              </Link>
            </div>
          )}
        </div>

        {/* Center Logo */}
        <Link
          className='flex items-center justify-center mt-16 w-1/3'
          to='/'
          onMouseEnter={() => setHoveredLink('Home')}
          onMouseLeave={() => setHoveredLink(null)}>
          <motion.img
            src={logo}
            alt='TheBookerLogo'
            className='h-32 mt-12'
            variants={logoVariants}
            initial='initial'
            animate='animate'
          />
        </Link>

        {/* Right Side */}
        <div className='flex items-center text-xl mt-16 w-1/3 px-12 justify-between'>
          {isRegisterPage && (
            <>
              <div
                className={`relative ${isRTL ? 'left-[20%]' : 'right-[20%]'}`}
                onMouseEnter={() => {
                  setHoveredLink('blogs');
                  handleDropdownToggle();
                }}
                onMouseLeave={() => {
                  setHoveredLink(null);
                  setShowDropdown(false);
                }}>
                <Link
                  to='#c'
                  className='text-white font-hurmeSemiBold pb-2 relative'>
                  {t('nav.blogs')}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transform transition-all duration-300 ${
                      hoveredLink === 'blogs' ? 'scale-x-100' : 'scale-x-0'
                    } origin-left`}></span>
                </Link>
                <div
                  className={`absolute left-0 mt-0 bg-booker border border-bookerGray border-opacity-30 rounded-lg shadow-lg py-1 w-[250px] z-50 transform transition-all duration-300 ease-in-out origin-top ${
                    showDropdown
                      ? 'scale-y-100 opacity-100'
                      : 'scale-y-0 opacity-0'
                  }`}>
                  <div className='pt-2'>
                    <a
                      href='/blog/5-Benefits-of-having-a-system-for-personal-care-business'
                      target='_blank'
                      className='block px-3 py-1.5 text-white text-sm hover:bg-bookerGray hover:bg-opacity-30 transition-colors duration-200'>
                      {t('nav.blogs1')}
                    </a>
                    <a
                      href='/blog/embracing-personal-care-together'
                      target='_blank'
                      className='block px-3 py-1.5 text-white text-sm hover:bg-bookerGray hover:bg-opacity-30 transition-colors duration-200'>
                      {t('nav.blogs2')}
                    </a>
                  </div>
                </div>
              </div>

              <div className='flex flex-col items-end h-32 relative mx-3'>
                {Translated && (
                  <div className='flex mt-4 text-2xl bg-bookerGray bg-opacity-30 rounded-full px-4 py-1'>
                    <button
                      className={`text-white font-hurmeSemiBold transition-colors duration-300 ${
                        i18n.language === 'en'
                          ? 'text-gray-200 font-hurmeBold'
                          : 'hover:text-gray-300'
                      }`}
                      onClick={() => changeLanguage('en')}>
                      EN
                    </button>
                    <span className='text-white mx-2'>|</span>
                    <button
                      className={`text-white font-hurmeSemiBold transition-colors duration-300 ${
                        i18n.language === 'ar'
                          ? 'text-gray-200 font-hurmeBold'
                          : 'hover:text-gray-300'
                      }`}
                      onClick={() => changeLanguage('ar')}>
                      عربي
                    </button>
                  </div>
                )}

                <Link
                  to='/store'
                  className='bg-gray-300 text-booker px-6 py-1 rounded-full font-hurmeBold text-xl hover:text-bookerGray hover:bg-gray-100 transition-colors duration-300 absolute whitespace-nowrap inline-flex items-center justify-center top-16 scale-110'>
                  {t('nav.book')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className='lg:hidden rounded-b-[4vh]'>
        <div
          className={`flex ${
            !isRegisterPage ? 'justify-center py-4' : 'justify-between p-4'
          } `}>
          <motion.img
            src={logo}
            alt='TheBookerLogo'
            className={`${isRegisterPage ? 'h-16' : 'h-20 relative top-14'}`}
            variants={logoVariants}
            initial='initial'
            animate='animate'
          />
          {isRegisterPage && (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className='text-white p-2 bg-bookerGray bg-opacity-30 rounded-md'>
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'>
                {isMenuOpen ? (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                ) : (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 6h16M4 12h16M4 18h16'
                  />
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Mobile Menu with Animation */}
        <div
          className={`bg-booker absolute w-full z-50 transition-all duration-300 ease-in-out overflow-hidden ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } rounded-b-[4vh]`}>
          <div className='p-4 space-y-4'>
            <Link
              to='/'
              onClick={() => setIsMenuOpen(false)}
              className='block text-white hover:text-gray-200 py-2 font-medium transform transition-transform duration-300 hover:translate-x-2'>
              {t('nav.home')}
            </Link>
            {/* <Link
              to='https://thebooker.online/#business'
              onClick={() => setIsMenuOpen(false)}
              className='block text-white hover:text-gray-200 py-2 font-medium transform transition-transform duration-300 hover:translate-x-2'>
              {t('nav.join')}
            </Link> */}
            <Link
              to='https://thebooker.online/#business'
              onClick={() => setIsMenuOpen(false)}
              className='block text-white hover:text-gray-200 py-2 font-medium transform transition-transform duration-300 hover:translate-x-2'>
              {t('nav.pricing')}
            </Link>
            <div className='relative'>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className='flex items-center justify-between w-full text-white hover:text-gray-200 py-2 font-medium transform transition-transform duration-300 hover:translate-x-2 font-hurmeSemiBold'>
                <span>{t('nav.blogs')}</span>
                <ArrowDown
                  className={`w-5 h-5 transition-transform duration-300 ${
                    showDropdown ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`bg-booker bg-opacity-30 rounded-lg mt-1 overflow-hidden transition-all duration-300 ${
                  showDropdown ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <a
                  href='/blog/5-Benefits-of-having-a-system-for-personal-care-business'
                  target='_blank'
                  onClick={() => setIsMenuOpen(false)}
                  className='block px-4 py-2 text-white text-sm hover:bg-bookerGray hover:bg-opacity-30 transition-colors duration-200'>
                  5 Benefits of Having a Booking System for Personal Care
                  Businesses
                </a>
                <a
                  href='/blog/embracing-personal-care-together'
                  target='_blank'
                  onClick={() => setIsMenuOpen(false)}
                  className='block px-4 py-2 text-white text-sm hover:bg-bookerGray hover:bg-opacity-30 transition-colors duration-200'>
                  Embracing Personal Care Together
                </a>
              </div>
            </div>
            <div className='flex items-center py-2 bg-bookerGray bg-opacity-30 rounded-full px-4 w-max'>
              <button
                className={`text-white px-2 transition-colors duration-300 ${
                  i18n.language === 'en' ? 'text-gray-200 font-hurmeBold' : ''
                }`}
                onClick={() => {
                  changeLanguage('en');
                  setIsMenuOpen(false);
                }}>
                EN
              </button>
              <span className='text-white mx-2'>|</span>
              <button
                className={`text-white px-2 transition-colors duration-300 ${
                  i18n.language === 'ar' ? 'text-gray-200 font-hurmeBold' : ''
                }`}
                onClick={() => {
                  changeLanguage('ar');
                  setIsMenuOpen(false);
                }}>
                عربي
              </button>
            </div>
            <Link
              to='/store'
              onClick={() => setIsMenuOpen(false)}
              className='block bg-gray-300 text-booker no-underline px-4 py-2 rounded-full text-center font-hurmeBold hover:bg-gray-100 transition-colors duration-300'>
              {t('nav.book')}
            </Link>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
